import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { CalendarDays, MapPin, ArrowDown } from "lucide-react";
import { useLang } from "../../i18n";
import { IMAGES } from "../../data";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      {/* gold hairline frame */}
      <div className="pointer-events-none absolute inset-4 z-20 hidden border border-gold/40 md:block" />

      <div className="mx-auto grid w-full max-w-[1440px] flex-1 grid-cols-1 items-center gap-10 px-6 pb-16 pt-32 md:px-14 lg:grid-cols-12 lg:gap-4 lg:pt-36">
        {/* kinetic typography */}
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-panettone"
            data-testid="hero-overline"
          >
            <span className="inline-block h-px w-10 bg-panettone" />
            {t.hero.overline}
          </motion.p>

          <h1 className="font-serif leading-[0.92]" data-testid="hero-title">
            <MaskedLine delay={0.35} className="text-outline text-[15vw] font-semibold tracking-tight sm:text-[11vw] lg:text-[7.2vw]">
              {t.hero.line1}
            </MaskedLine>
            <MaskedLine delay={0.5} className="text-[16vw] font-medium italic text-panettone sm:text-[12vw] lg:text-[7.8vw]">
              {t.hero.line2}
            </MaskedLine>
            <MaskedLine delay={0.65} className="text-[15vw] font-semibold tracking-tight text-ink sm:text-[11vw] lg:text-[7.2vw]">
              {t.hero.line3}
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col gap-6"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-10">
              <p className="flex items-center gap-2.5 text-base font-semibold text-ink" data-testid="hero-dates">
                <CalendarDays size={18} className="text-panettone" />
                {t.hero.dates}
              </p>
              <p className="flex items-center gap-2.5 text-base text-ink/70" data-testid="hero-venue">
                <MapPin size={18} className="text-panettone" />
                <span>
                  {t.hero.venue} · <span className="whitespace-nowrap">{t.hero.address}</span>
                </span>
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href="#participate"
                data-testid="hero-cta-visitor"
                className="group relative overflow-hidden bg-panettone px-8 py-4 text-center text-[12px] font-semibold uppercase tracking-[0.2em] text-cream"
              >
                <span className="relative z-10 transition-colors duration-500 group-hover:text-ink">{t.hero.ctaVisitor}</span>
                <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
              </a>
              <a
                href="#participate"
                data-testid="hero-cta-exhibitor"
                className="border border-ink/30 px-8 py-4 text-center text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-500 hover:border-panettone hover:bg-panettone hover:text-cream"
              >
                {t.hero.ctaExhibitor}
              </a>
            </div>
          </motion.div>
        </motion.div>

        {/* clipped photography + floating cutout */}
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="spotlight relative h-[46vh] overflow-hidden lg:h-[68vh]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Artisanal panettone"
              style={{ y: imgY }}
              className="h-[115%] w-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-10 -left-6 z-10 w-28 sm:w-36 lg:-left-12 lg:w-44"
          >
            <motion.svg
              viewBox="0 0 120 120"
              animate={{ rotate: 360 }}
              transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
              className="drop-shadow-[0_18px_24px_rgba(26,22,20,0.35)]"
              data-testid="hero-edition-badge"
            >
              <defs>
                <path id="badge-circle" d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0" />
              </defs>
              <circle cx="60" cy="60" r="58" fill="#1A1614" />
              <circle cx="60" cy="60" r="58" fill="none" stroke="#D4AF37" strokeOpacity="0.5" strokeWidth="1" />
              <text fill="#D4AF37" fontSize="9" letterSpacing="1.6" fontFamily="Inter, sans-serif" fontWeight="600">
                <textPath href="#badge-circle">{`NEW YORK PANETTONE FESTIVAL · ${t.hero.edition} · `}</textPath>
              </text>
              <g transform="translate(44,42) scale(0.52)">
                <path d="M32 11c-9.5 0-16.5 6.2-16.5 14.5V27h33v-1.5C48.5 17.2 41.5 11 32 11z" fill="#E2A85A" />
                <circle cx="24" cy="18" r="1.7" fill="#1A1614" />
                <circle cx="33" cy="15.5" r="1.7" fill="#1A1614" />
                <circle cx="40" cy="20" r="1.7" fill="#1A1614" />
                <path d="M17.5 27h29L43 50.5a4.5 4.5 0 0 1-4.5 4H25.5a4.5 4.5 0 0 1-4.5-4L17.5 27z" fill="#8B1C1C" />
                <path d="M23 27l1.4 27M32 27v27.4M41 27l-1.4 27" stroke="#FDFBF7" strokeOpacity="0.35" strokeWidth="1.6" />
              </g>
            </motion.svg>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="absolute -top-8 right-0 hidden text-[10px] font-semibold uppercase tracking-[0.4em] text-ink/50 lg:block"
          >
            {t.hero.edition}
          </motion.p>
        </div>
      </div>

      {/* editorial marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 border-t border-ink/15 py-4"
        data-testid="hero-marquee"
      >
        <Marquee speed={28} gradient={false} pauseOnHover>
          {t.marquee.map((item, i) => (
            <span key={i} className="mx-8 flex items-center gap-8 font-serif text-lg italic text-ink/70">
              {item}
              <span className="text-gold not-italic">✦</span>
            </span>
          ))}
        </Marquee>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-20 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-1 text-ink/40 lg:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
